import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChevronDown, Loader2, MoreHorizontal, Plus, Printer, Search, Share2 } from 'lucide-react'
import toast from 'react-hot-toast'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useAuthStore } from '@/store/auth.store'
import { invoicesService } from '@/services/invoicesService'
import type { InvoiceListItem } from '@/api/types'
import { formatCalendarDate } from '@/lib/calendarDate'
import { formatCurrency } from '@/lib/format'
import { formatOverdueDuration, getDocumentPayStatus, type DocumentPayStatus } from '@/lib/payStatus'
import InvoicePrint from '@/modules/invoices/pages/Print'

function invoiceKey(item: InvoiceListItem): number {
  return Number(item.invoiceId ?? item.id ?? 0)
}

function formatInvoiceDisplayNo(raw?: string | number | null): string {
  if (raw === undefined || raw === null || String(raw).trim() === '') return ''
  const cleaned = String(raw).trim().replace(/^INV\s*-?\s*/i, '')
  const digits = cleaned.replace(/\D/g, '')
  if (!digits) return `INV - ${cleaned}`
  return `INV - ${digits.padStart(6, '0')}`
}

function invoiceNo(item: InvoiceListItem): string {
  return formatInvoiceDisplayNo(item.invoiceNumber || item.invoiceNo || invoiceKey(item)) || '—'
}

function StatusRibbon({ label, className }: { label: string; className: string }) {
  return (
    <div className="pointer-events-none absolute left-0 top-0 z-20 h-[4.75rem] w-[4.75rem] overflow-hidden">
      <div
        className={`absolute -left-7 top-[14px] w-[7.75rem] -rotate-45 py-0.5 text-center text-[10px] font-bold uppercase tracking-widest text-white shadow ${className}`}
      >
        {label}
      </div>
    </div>
  )
}

function invoicePayStatus(inv: InvoiceListItem): DocumentPayStatus {
  return getDocumentPayStatus({
    payStatus: inv.payStatus,
    remainingAmount: inv.remainingAmount,
    paidAmount: inv.paidAmount,
    total: inv.total ?? inv.amount,
    dueDate: inv.dueDate,
  })
}

export const InvoiceWorkspace: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId
  const selectedId = Number(id)

  const [invoices, setInvoices] = useState<InvoiceListItem[]>([])
  const [loadingList, setLoadingList] = useState(true)
  const [query, setQuery] = useState('')

  useEffect(() => {
    if (!branchId) return
    let cancelled = false
    const load = async () => {
      try {
        setLoadingList(true)
        const response = await invoicesService.getInvoices(branchId, 1, 500, 'desc')
        if (!cancelled) setInvoices(response.data || [])
      } catch {
        if (!cancelled) setInvoices([])
      } finally {
        if (!cancelled) setLoadingList(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [branchId])

  const selected = useMemo(
    () => invoices.find((inv) => invoiceKey(inv) === selectedId),
    [invoices, selectedId],
  )

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return invoices
    return invoices.filter((inv) =>
      [invoiceNo(inv), inv.customerName, formatCalendarDate(inv.invoiceDate)]
        .join(' ')
        .toLowerCase()
        .includes(q),
    )
  }, [invoices, query])

  const payStatus: DocumentPayStatus | null = selected ? invoicePayStatus(selected) : null

  const isPosted =
    selected?.invStatus === 'Posted' ||
    selected?.isPosted === true ||
    selected?.isPosted === 1 ||
    selected?.status?.toLowerCase() === 'posted'

  const paymentsCount = Number(selected?.paidAmount ?? 0) > 0 ? 1 : 0

  const handleSelect = (inv: InvoiceListItem) => {
    const nextId = invoiceKey(inv)
    if (!nextId || nextId === selectedId) return
    navigate(`/invoices/${nextId}/view`)
  }

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href)
      toast.success(t('invoices.shareLinkCopied'))
    } catch {
      toast.error(t('invoices.failedToLoadInvoice'))
    }
  }

  const listPayLabel = (inv: InvoiceListItem) => {
    const status = invoicePayStatus(inv)
    if (status === 'paid') return t('invoices.paid')
    if (status === 'overdue') {
      const duration = formatOverdueDuration(inv.dueDate, t)
      return duration ? t('invoices.overdueBy', { duration }) : t('invoices.overdue')
    }
    return t('invoices.notPaid')
  }

  const listPayClass = (inv: InvoiceListItem) => {
    const status = invoicePayStatus(inv)
    if (status === 'paid') return 'text-emerald-600'
    if (status === 'overdue') return 'text-red-600'
    return 'text-amber-600'
  }

  return (
    <div className="flex h-[calc(100vh-5.5rem)] min-h-0 bg-[#f4f6fb]">
      <aside className="flex w-[320px] shrink-0 flex-col border-r border-slate-200 bg-white">
        <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2.5">
          <button type="button" className="flex items-center gap-1 text-sm font-semibold text-slate-800">
            {t('invoices.allInvoices')}
            <ChevronDown className="h-4 w-4 text-slate-500" />
          </button>
          <div className="flex items-center gap-1">
            <Button size="icon" className="h-8 w-8 bg-[#3b82f6] hover:bg-[#2563eb]" onClick={() => navigate('/invoices/new')}>
              <Plus className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate('/invoices')}>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="border-b border-slate-200 px-3 py-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('invoices.searchInvoices')}
              className="h-8 pl-8 text-sm"
            />
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {loadingList ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-muted-foreground">{t('invoices.noInvoicesFound')}</p>
          ) : (
            filtered.map((inv) => {
              const key = invoiceKey(inv)
              const active = key === selectedId
              return (
                <button
                  key={key || invoiceNo(inv)}
                  type="button"
                  onClick={() => handleSelect(inv)}
                  className={`w-full border-b border-slate-100 px-4 py-3 text-left transition-colors ${
                    active ? 'bg-[#e8eeff]' : 'hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className="truncate text-sm font-medium text-slate-800">{inv.customerName || '—'}</p>
                    <p className="shrink-0 text-sm font-semibold text-slate-900">
                      {formatCurrency(Number(inv.total ?? inv.amount ?? 0))}
                    </p>
                  </div>
                  <p className="mt-1 text-xs text-slate-500">
                    {invoiceNo(inv)} · {formatCalendarDate(inv.invoiceDate)}
                  </p>
                  <p className={`mt-1.5 text-[11px] font-bold uppercase tracking-wide ${listPayClass(inv)}`}>
                    {listPayLabel(inv)}
                  </p>
                </button>
              )
            })
          )}
        </div>
      </aside>

      <section className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center justify-between border-b border-slate-200 bg-white px-5 py-3">
          <h1 className="text-xl font-semibold tracking-tight text-slate-900">
            {selected ? invoiceNo(selected) : formatInvoiceDisplayNo(selectedId) || '—'}
          </h1>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="h-8" onClick={() => void handleShare()}>
              <Share2 className="mr-2 h-4 w-4" />
              {t('invoices.share')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              onClick={() => selectedId && window.open(`/invoices/${selectedId}/print?autoprint=1`, '_blank')}
            >
              <Printer className="mr-2 h-4 w-4" />
              {t('invoices.pdfPrint')}
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <button
          type="button"
          className="flex w-full items-center justify-between border-b border-slate-200 bg-white px-5 py-2.5 text-left text-sm"
        >
          <span className="font-medium text-slate-800">{t('invoices.paymentsReceived')}</span>
          <span className="flex items-center gap-2">
            <span className="text-sm font-semibold text-[#3b82f6]">{paymentsCount}</span>
            <ChevronDown className="h-4 w-4 text-slate-400" />
          </span>
        </button>

        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border-b border-slate-200 bg-white px-5 py-3 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-slate-700">{t('invoices.status')}</span>
            <span
              className={`text-sm font-semibold ${
                isPosted ? 'text-blue-800 dark:text-blue-300' : 'text-sky-500 dark:text-sky-400'
              }`}
            >
              {isPosted ? t('invoices.posted') : t('invoices.notPosted')}
            </span>
          </div>
          <div className="text-slate-700">
            {t('invoices.invoiceType')}:{' '}
            <span className="font-medium">{selected?.type || t('invoices.taxInvoice')}</span>
          </div>
        </div>

        {isPosted && (
          <p className="border-b border-slate-200 bg-white px-5 py-2 text-sm text-slate-500">{t('invoices.qrGenerated')}</p>
        )}

        <div className="min-h-0 flex-1 overflow-auto p-6">
          <div className="relative mx-auto max-w-[860px] overflow-hidden rounded-sm border border-slate-200 bg-white shadow-sm">
            {payStatus === 'paid' && (
              <StatusRibbon label={t('invoices.paid')} className="bg-emerald-500" />
            )}
            {payStatus === 'overdue' && (
              <StatusRibbon label={t('invoices.overdue')} className="bg-red-500" />
            )}
            {payStatus === 'notPaid' && (
              <StatusRibbon label={t('invoices.notPaid')} className="bg-amber-500" />
            )}
            <div className={payStatus ? '[&_.invoice-wrapper]:!px-14 [&_.invoice-wrapper]:!pt-6' : undefined}>
              {selectedId > 0 && <InvoicePrint key={selectedId} embedded />}
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default InvoiceWorkspace
